import cn from 'classnames';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { Link, NavLink, useHistory, useLocation } from 'react-router-dom';

import { ArrowLeftIcon, BackwardArrow, HomeOutline, SearchIcon } from 'assets/icons';
import { handleBalanceData } from 'features/customerPanel/store/cpPanel.action';
import { AKO, API_BASE_URL, APP_URL, BONUS_BALANCE, SectionNavigation } from 'shared/constants/common';
import { BalanceState, BETSType, State } from 'shared/interface';
import { addBet, changeBetMode } from 'shared/store/coupon.action';
import { calcTotalBalance, getBalanceDetails, getUserDetails, handleAddOverflow, isLoggedin, sideNavLinks } from 'shared/util/utility';

import Logo from 'assets/images/logo.svg';
import ABTestRegistration from 'features/auth/ABTestRegistration';
import ABTestVerification from 'features/auth/ABTestVerification';
import CpHeader from 'features/customerPanel/cpHeader/cpHeader';
import Panel from 'features/customerPanel/panel/component/panel';
import LoginModal from 'features/login/component/loginModal';
import RemindPassword from 'features/login/component/remindPassword';
import { showLoginModal } from 'features/login/store/login.action';
import { OffersState } from 'features/offers/interface/store.interface';
import Registration from 'features/registration/container';
import Searchbar from 'features/Searchbar';
import BottomDrawer from 'shared/components/BottomDrawer';
import Button from 'shared/components/form/button';
import ThemeSwitch from 'shared/components/ThemeSwitch';
import { LoginEvents, PaymentEvents, RegisterEvents, SearchEvents, setDataLayer } from 'shared/constants/datalayer';
import { useNewRegistrationPanelFlag } from 'shared/featureFlags';
import useClickOutside from 'shared/hooks/useClickOutside';
import { useDeposit } from 'shared/hooks/useDeposit';
import { useNewMobileUiManager } from 'shared/hooks/useNewMobileUiManager';
import useSearchBar from 'shared/hooks/useSearchbar';
import useSectionNavigation from 'shared/hooks/useSectionNavigation';
import { useVipUserCheck } from 'shared/hooks/useVipUserCheck';
import { getUnreadMessage } from 'shared/store/navigation.action';
import { GetHeaderData } from './getHeaderData';
import HeaderCouponBtn from './header-coupon-btn';
import HeaderBalance from './headerBalance';

interface Props {
	isPopupWithRoute?: boolean;
	numOfSlipsInBetslip: number | string;
	isShowInstall: boolean;
	isMobile: boolean;
}

const Header: React.FC<Props> = ({ isMobile, isShowInstall, isPopupWithRoute }) => {
	const { t } = useTranslation();
	const history = useHistory();
	const location = useLocation();
	const isUser = isLoggedin();
	const dispatch = useDispatch();
	const { isViewActive, isMainView, changeNavigationView, goToFirstNavigationView } = useSectionNavigation();
	const { vipUserInitalsBgClass } = useVipUserCheck();
	const { closeSearchModal } = useSearchBar();
	const balanceData = useSelector<State, BalanceState>((state) => state.cpPanel.balanceData);
	const offers = useSelector<State, OffersState>((state) => state.offers);
	const { freebetsActive, bonusesQueued } = offers;
	const unreadMessageCount = useSelector<State, number>((state) => state.navigation.unreadMessages.messageCount);
	const messagesToDeleteCount = useSelector<State, number>((state) => state.cpPanel.messagesToDeleteCount);
	const count = useSelector<State, any>((state) => state.layout.count);
	const { mainBalance, bonusBalance } = balanceData as {
		mainBalance: number;
		bonusBalance: number;
	};
	const bets = useSelector<State, BETSType>((state) => state.coupon.bets);
	const userAvatar = useSelector((state: RootStateOrAny) => state.playerProfile.avatar);
	const isRegistrationOpen = useSelector((state: State) => state.registerModal.step1RegisterModalOpen);
	const inGameCouponCount = useSelector<State, number>((state) => state.inGameCouponCount.inGameCouponCount);
	const [showBalanceBar, setShowBalanceBar] = useState<boolean>(false);
	const [totalBalance, setTotalBalance] = useState<number | undefined>(0);
	const [isLoginModalVisible, setIsLoginModalVisible] = useState<boolean>(false);
	const [isDrawerOpen, setDrawerOpen] = useState<boolean>(false);
	const { openDepositPanel } = useDeposit();
	const showNewUIStatus = useNewMobileUiManager();
	const showNewUI = window.location.pathname === APP_URL.home ? false : showNewUIStatus;
	const handleClickOutside = () => {
		setShowBalanceBar(false);
	};
	const newRegistrationPanelFlag = useNewRegistrationPanelFlag();
	const localNewRegistrationPanelFlag = newRegistrationPanelFlag && newRegistrationPanelFlag.value;

	const headerBalanceRef = useClickOutside(handleClickOutside, 'click');

	const headerData = GetHeaderData(isPopupWithRoute, messagesToDeleteCount) || {
		navBack: '',
		title: '',
		navType: '',
	};

	const toggleLoginModal = () => {
		setIsLoginModalVisible(!isLoginModalVisible);
	};

	const handleOpenLoginModal = () => {
		dispatch(showLoginModal());
		DataLayerLogin();
	};

	const DataLayerLogin = () => {
		if (isMobile) {
			history.push(APP_URL.login, window.location.pathname);
		}
		setDataLayer(LoginEvents.DATALAYER_LOGIN_START);
		setDataLayer(RegisterEvents.DATALAYER_RELOGIN_START);
	};

	const Register = () => {
		if (isMobile) {
			history.push(APP_URL.register, window.location.pathname);
		} else {
			goToFirstNavigationView(SectionNavigation.REGISTRATION_METHOD);
		}
	};

	const handleBalanceChange = (activeTab?: string) => {
		dispatch(
			handleBalanceData({
				...getBalanceDetails(),
				selectedBalanceType: activeTab,
			}),
		);

		const isBonusActivated = activeTab === BONUS_BALANCE;
		if (isBonusActivated) {
			dispatch(changeBetMode(AKO));
		}
		dispatch(addBet({ ...bets, bonusBet: isBonusActivated && !bets.isFreeBetSelected }));

		if (activeTab) {
			setShowBalanceBar(false);
		}
	};

	const handleShowUserPanel = () => {
		changeNavigationView(SectionNavigation.USER);
		handleAddOverflow();

		if (!isMobile) {
			document.body.classList.add('cp-open');
		}
	};

	const isElementActive = (currentUrl: string, link: string) => {
		if (currentUrl.includes(link)) {
			// default behavior of routes
			return 'active';
		}
		if (
			currentUrl.includes(APP_URL.home) &&
			currentUrl !== APP_URL.home &&
			!currentUrl.includes(APP_URL.promotion) &&
			!currentUrl.includes(APP_URL.entertainment) &&
			link.includes(APP_URL.sport)
		) {
			// If current url is not home and not promotion page, then sport page is active.
			// This solution is for 2nd (or deeper) level pages.
			return 'active';
		}
		return '';
	};

	useEffect(() => {
		if (isLoggedin()) {
			const totalBalance = calcTotalBalance(mainBalance, bonusBalance, freebetsActive, bonusesQueued);
			setTotalBalance(totalBalance);
			dispatch(getUnreadMessage());
		}
	}, [mainBalance, bonusBalance, freebetsActive, bonusesQueued, dispatch]);

	const navigateBack = () => {
		if (isPopupWithRoute) {
			history.push(APP_URL.home);
		} else {
			history.goBack();
		}
	};

	const scrollToTop = () => {
		if (location.pathname === APP_URL.home) window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	const handleCloseBottomDrawer = () => {
		setDrawerOpen(false);
		closeSearchModal();
		setDataLayer(SearchEvents.DATALAYER_SEARCH_CLOSE);
	};

	const handleOpenDrawer = () => {
		setDrawerOpen(true);
	};

	const getEnvironmentName = () => {
		if (API_BASE_URL?.includes('test-app')) {
			return 'TEST';
		}

		if (API_BASE_URL?.includes('rc-app')) {
			return 'RC';
		}

		if (API_BASE_URL?.includes('localhost')) {
			return 'LOCAL';
		}

		return null;
	};

	const openDeposit = () => {
		PaymentEvents.DEPO_START({ depo_origin: ['nawigacja'] });
		openDepositPanel();
	};

	return (
		<>
			{isMobile && (
				<header className={`main-header ${isShowInstall ? 'sub-header' : ''}`}>
					{isViewActive(SectionNavigation.REGISTRATION_METHOD) && <ABTestRegistration />}
					<div className={`header-wrapper ${!isPopupWithRoute && isViewActive(SectionNavigation.HOME) ? 'main-header-shadow' : ''}`}>
						{isViewActive(SectionNavigation.USER) && <CpHeader onGoBack={() => history.goBack()} title={headerData.title as string} />}
						{!isPopupWithRoute && isMainView() && (
							<div
								className={cn('h-full flex items-center justify-between gap-4 relative', {
									'w-full mr-2': showNewUI || showNewUIStatus,
								})}
							>
								{showNewUI ? (
									<div className="flex ml-2 gap-6 items-center max-w-34 w-full">
										<ArrowLeftIcon onClick={navigateBack} className="pointer" />
										<HomeOutline onClick={() => location.pathname !== APP_URL.home && history.push(APP_URL.home)} />
										{isUser && !isPopupWithRoute && isMainView() && <SearchIcon onClick={handleOpenDrawer} />}
									</div>
								) : (
									<div className="flex gap-3 items-center">
										<Link to={APP_URL.home} className="relative" aria-label="site-logo">
											<div className="logo" onClick={scrollToTop}>
												<img src={Logo} alt="Zakłady bukmacherskie" height={33} width={40} />
											</div>
											{getEnvironmentName() && (
												<span className="bg-error-600 rounded-4 w-auto px-1 py-[6px] text-white absolute -top-2 -right-3 flex items-center justify-center text-label-2xs font-bold">
													{getEnvironmentName()}
												</span>
											)}
										</Link>
										{!showNewUI && isUser && <SearchIcon onClick={handleOpenDrawer} />}
									</div>
								)}
							</div>
						)}

						{(isPopupWithRoute || !isMainView()) &&
							!isViewActive(SectionNavigation.USER) &&
							(showNewUI ? (
								<div className="left-link__wrapper pointer" onClick={navigateBack}>
									<div className="flex items-center gap-2">
										<ArrowLeftIcon onClick={navigateBack} />
										<div>{t('quit')}</div>
									</div>
									<span
										id="custom-header-title"
										className="header-component-title !font-semibold !ml-auto absolute left-0 right-0 flex items-center justify-center"
									>
										{headerData.title}
									</span>
								</div>
							) : (
								<div className="left-link__wrapper" onClick={navigateBack}>
									<div className="back-button">
										<BackwardArrow className="fill-white cursor-pointer" />
									</div>
									<span id="custom-header-title" className="header-component-title">
										{headerData.title}
									</span>
								</div>
							))}
						{!isPopupWithRoute && (isMainView() || isViewActive(SectionNavigation.COUPON)) && (
							<div className="header-right-section">
								{isUser ? (
									<div className="header-loggedin-user">
										<HeaderBalance
											isUser={isUser}
											isMobile={isMobile}
											handleTabChange={handleBalanceChange}
											mainBalance={mainBalance}
											headerBalanceRef={headerBalanceRef}
											handleAddFundsButton={openDeposit}
											bonusBalance={bonusBalance}
											setShowBalanceBar={setShowBalanceBar}
											showBalanceBar={showBalanceBar}
											totalBalance={totalBalance}
										/>
									</div>
								) : (
									<div className="headerButton__wrapper">
										<Button
											btnType="teritary"
											btnHeight="medium"
											id="header-login-btn"
											cyTestRef="header-login-button"
											className="header__loginButton"
											onClick={DataLayerLogin}
										>
											{t('signIn')}
										</Button>
										<Button
											btnType="secondary"
											btnHeight="small"
											id="header-login-btn"
											cyTestRef="header-login-button"
											className="header__registerButton"
											onClick={() => {
												localNewRegistrationPanelFlag ? history.push(APP_URL.register, window.location.pathname) : Register();
											}}
										>
											{t('signUp')}
										</Button>
									</div>
								)}
							</div>
						)}
						{!isPopupWithRoute && (isMainView() || isViewActive(SectionNavigation.COUPON)) && isUser && (
							<>
								<div className="header-loggedin-user">
									{!showNewUI && !showNewUIStatus && <ThemeSwitch />}
									<Button
										className={`header-user-initials header-button--round header-button--round-text ${vipUserInitalsBgClass}`}
										onClick={handleShowUserPanel}
										cyTestRef="initials-button"
									>
										{userAvatar.length ? (
											<img src={userAvatar} alt="userAvatar" className="header-user-initials__avatar" />
										) : (
											getUserDetails()?.firstName[0] + getUserDetails()?.lastName[0]
										)}

										{unreadMessageCount > 0 && <span className="count position--absolute">{unreadMessageCount}</span>}
									</Button>
								</div>
							</>
						)}
					</div>
				</header>
			)}

			{!isMobile && (
				<header className={`main-header web-header ${isShowInstall ? 'sub-header' : ''}`}>
					<LoginModal />
					<RemindPassword />
					{isViewActive(SectionNavigation.ACCOUNT_VERIFICATION_METHOD) && <ABTestVerification />}
					{isViewActive(SectionNavigation.REGISTRATION_METHOD) && <ABTestRegistration />}
					{isRegistrationOpen && <Registration />}
					<div className="header-box">
						<div className={`header-wrapper ${isViewActive(SectionNavigation.HOME) ? 'main-header-shadow' : ''}`}>
							<div className="left-side-links">
								<Link className="flex relative" to={APP_URL.home}>
									<img src={Logo} className="desktop-logo" alt="Zakłady bukmacherskie" />
									{getEnvironmentName() && (
										<span className="bg-error-600 rounded-4 w-auto px-1 py-[6px] text-white absolute -top-1 -right-2 flex items-center justify-center text-label-2xs font-bold">
											{getEnvironmentName()}
										</span>
									)}
								</Link>
								<ul className="header_links">
									<NavLink
										exact
										to={APP_URL.home}
										className={`tab ${location.pathname === APP_URL.home ? 'active' : ''} header-link`}
									>
										{t('home')}
									</NavLink>
									{sideNavLinks.map((item) => (
										<NavLink
											className={`tab ${isElementActive(location.pathname, item.link)} rest-header-tabs`}
											key={`sideNavLinks-${item.link}`}
											to={item.link}
											data-cy="header-tab"
										>
											{t(item.name)}
											{item.name === 'live' && (
												<span className={`count ${count > 99 ? 'p--2' : ''} count__header`}>{count}</span>
											)}
										</NavLink>
									))}
								</ul>
							</div>

							{(isMainView() || isViewActive(SectionNavigation.COUPON) || !isMobile) && (
								<div onClick={toggleLoginModal} className="header-right-section header__themeSwticher">
									<ThemeSwitch />
									{isUser ? (
										<div className="header-loggedin-user">
											<HeaderBalance
												isUser={isUser}
												isMobile={isMobile}
												handleTabChange={handleBalanceChange}
												mainBalance={mainBalance}
												headerBalanceRef={headerBalanceRef}
												handleAddFundsButton={openDeposit}
												bonusBalance={bonusBalance}
												setShowBalanceBar={setShowBalanceBar}
												showBalanceBar={showBalanceBar}
												totalBalance={totalBalance}
											/>
											<HeaderCouponBtn couponCount={inGameCouponCount} redirectTo={APP_URL.coupon} />
											<Button
												className={`header-button--round header-button--round-text header-user-initials ${vipUserInitalsBgClass}`}
												onClick={handleShowUserPanel}
												cyTestRef="initials-button"
											>
												{userAvatar.length ? (
													<img src={userAvatar} alt="userAvatar" className="header-user-initials__avatar" />
												) : (
													getUserDetails()?.firstName[0] + getUserDetails()?.lastName[0]
												)}

												{unreadMessageCount > 0 && (
													<span className="count  header-button--round__count header-button--round__count-text position--absolute">
														{unreadMessageCount}
													</span>
												)}
											</Button>
										</div>
									) : (
										<div className="headerButton__wrapper">
											<Button
												btnType="teritary"
												btnHeight="medium"
												id="header-login-btn"
												cyTestRef="header-register-button"
												className="header__loginButton"
												onClick={handleOpenLoginModal}
											>
												{t('signIn')}
											</Button>
											<Button
												btnType="secondary"
												btnHeight="small"
												id="header-login-btn"
												cyTestRef="header-register-button"
												className="header__registerButton"
												onClick={() => {
													localNewRegistrationPanelFlag
														? history.push(APP_URL.register, window.location.pathname)
														: Register();
												}}
											>
												{t('signUp')}
											</Button>
										</div>
									)}
								</div>
							)}
						</div>
					</div>
				</header>
			)}
			{isUser && (
				<BottomDrawer
					isBottomDrawer={isDrawerOpen}
					className="Searchbar__drawer"
					handleBottomDrawer={handleCloseBottomDrawer}
					cyCloseModalRef="searchbar-close"
				>
					<Searchbar handleCloseBottomDrawer={handleCloseBottomDrawer} isDrawerOpen={isDrawerOpen} />
				</BottomDrawer>
			)}
			<Panel balanceData={balanceData} showPanel={isViewActive(SectionNavigation.USER, isMobile) && isLoggedin()} />
		</>
	);
};

export default Header;
